import React, { useState, useEffect, useCallback } from 'react';
import { adminProducts, categoriesAPI, adminUploads, productsAPI } from '../../utils/api';
import toast from 'react-hot-toast';

const MAX_IMAGES = 6;

const emptyProduct = { name:'',name_ar:'',slug:'',description:'',short_description:'',category_id:'',base_price:'',compare_price:'',sku:'',origin_country:'',video_url:'',stock_quantity:0,is_active:1,is_featured:0,is_bestseller:0,variants:[] };

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  // images: array of { url, alt? } — first item is the primary image
  const [images, setImages] = useState([]);
  const [videoUrl, setVideoUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    adminProducts.getAll({ page, search, limit: 15 })
      .then(d => { setProducts(d.products); setTotal(d.total); setPages(d.pages); })
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { categoriesAPI.getAll().then(setCategories); }, []);

  const openNew = () => {
    setForm(emptyProduct);
    setEditing(null);
    setImages([]);
    setVideoUrl('');
    setShowForm(true);
  };

  const openEdit = async (p) => {
    setForm({ ...p, variants: [] });
    setEditing(p.id);
    setVideoUrl(p.video_url || '');
    // Fetch full product detail to get images array
    try {
      const detail = await productsAPI.getBySlug(p.slug);
      const imgs = (detail.images || []).map(i => ({ url: i.image_url, alt: i.alt_text || '' }));
      setImages(imgs.length ? imgs : (p.primary_image ? [{ url: p.primary_image, alt: '' }] : []));
    } catch {
      setImages(p.primary_image ? [{ url: p.primary_image, alt: '' }] : []);
    }
    setShowForm(true);
  };

  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    if (images.length + files.length > MAX_IMAGES) {
      toast.error(`Maximum ${MAX_IMAGES} images allowed`);
      return;
    }
    setUploadingImage(true);
    try {
      for (const file of files) {
        if (file.size > 100 * 1024 * 1024) { toast.error(`${file.name}: must be under 100MB`); continue; }
        const result = await adminUploads.productImage(file);
        setImages(prev => [...prev, { url: result.url, alt: '' }]);
      }
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = (idx) => {
    setImages(prev => prev.filter((_, i) => i !== idx));
  };

  const moveImage = (idx, dir) => {
    setImages(prev => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const handleVideoSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) { toast.error('Video must be under 100MB'); return; }
    setUploadingVideo(true);
    try {
      const result = await adminUploads.productVideo(file);
      setVideoUrl(result.url);
    } catch (err) {
      toast.error(err.message || 'Video upload failed');
    } finally {
      setUploadingVideo(false);
    }
  };

  const clearVideo = () => setVideoUrl('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? (checked ? 1 : 0) : value }));
  };

  const autoSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, video_url: videoUrl || null };
      // Numeric fields: empty string from <input> means "unset" — send null so MySQL
      // stores NULL instead of coercing to 0 (which would silently disable the discount badge).
      ['compare_price', 'category_id', 'harvest_year'].forEach(k => {
        if (payload[k] === '' || payload[k] === undefined) payload[k] = null;
      });
      // Stock: empty string = 0 (not null — schema is INT NOT NULL DEFAULT 0)
      if (payload.stock_quantity === '' || payload.stock_quantity === undefined || payload.stock_quantity === null) {
        payload.stock_quantity = 0;
      } else {
        payload.stock_quantity = Math.max(0, parseInt(payload.stock_quantity, 10) || 0);
      }
      // Always send images array — backend deletes-and-replaces on update
      payload.images = images.map((img, i) => ({
        image_url: img.url,
        alt_text: img.alt || null,
        is_primary: i === 0 ? 1 : 0,
        sort_order: i,
      }));

      if (editing) {
        await adminProducts.update(editing, payload);
        toast.success('Product updated!');
      } else {
        await adminProducts.create(payload);
        toast.success('Product created!');
      }
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}" permanently? This cannot be undone.`)) return;
    try {
      await adminProducts.delete(id);
      toast.success('Product deleted');
      load();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div>
      <div className="admin-page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div>
          <h2 style={{ fontFamily:'var(--font-serif)', fontSize:'1.5rem' }}>Products <span style={{ fontSize:'1rem', color:'var(--text-muted)', fontFamily:'var(--font-sans)' }}>({total})</span></h2>
        </div>
        <button className="btn btn-primary" onClick={openNew}>+ Add Product</button>
      </div>

      <div style={{ display:'flex', gap:12, marginBottom:16 }}>
        <input className="form-control" placeholder="Search products, SKU..." value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }} style={{ maxWidth:320 }} />
      </div>

      <div className="admin-card">
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr><th>Product</th><th>Category</th><th>Price</th><th>Stock</th><th>SKU</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" style={{ textAlign:'center', padding:40 }}><div className="spinner" style={{ margin:'0 auto' }} /></td></tr>
              ) : products.map(p => (
                <tr key={p.id}>
                  <td>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ width:40, height:40, borderRadius:8, background:'var(--cream)', overflow:'hidden', flexShrink:0 }}>
                        {p.primary_image ? <img src={p.primary_image} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : <span style={{ fontSize:'1.2rem', display:'flex', height:'100%', alignItems:'center', justifyContent:'center' }}>🍯</span>}
                      </div>
                      <div>
                        <p style={{ fontWeight:600, fontSize:'0.88rem' }}>{p.name}</p>
                        {p.name_ar && <p style={{ fontSize:'0.75rem', color:'var(--text-muted)', direction:'rtl' }}>{p.name_ar}</p>}
                      </div>
                    </div>
                  </td>
                  <td style={{ fontSize:'0.82rem', color:'var(--text-muted)' }}>{p.category_name || '—'}</td>
                  <td><strong>KD {parseFloat(p.base_price).toFixed(3)}</strong></td>
                  <td>
                    {(() => {
                      const s = parseInt(p.stock_quantity, 10);
                      if (!Number.isFinite(s)) return <span style={{ color:'var(--text-muted)' }}>—</span>;
                      const color = s <= 0 ? '#dc2626' : s <= 5 ? '#d97706' : 'var(--olive)';
                      return <strong style={{ color }}>{s}</strong>;
                    })()}
                  </td>
                  <td style={{ fontSize:'0.78rem', fontFamily:'monospace' }}>{p.sku || '—'}</td>
                  <td>
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                      {p.is_active ? <span className="badge badge-olive">Active</span> : <span className="badge badge-red">Inactive</span>}
                      {p.is_featured ? <span className="badge badge-gold">Featured</span> : null}
                      {p.is_bestseller ? <span className="badge" style={{ background:'rgba(159,122,234,0.15)', color:'#6b46c1' }}>Bestseller</span> : null}
                    </div>
                  </td>
                  <td>
                    <div style={{ display:'flex', gap:8 }}>
                      <button className="action-btn" onClick={() => openEdit(p)}>Edit</button>
                      <button className="action-btn action-btn--danger" onClick={() => handleDelete(p.id, p.name)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pages > 1 && (
          <div className="admin-pagination">
            <button disabled={page===1} onClick={() => setPage(p=>p-1)}>← Prev</button>
            <span>Page {page} of {pages}</span>
            <button disabled={page===pages} onClick={() => setPage(p=>p+1)}>Next →</button>
          </div>
        )}
      </div>

      {/* Product Form Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal modal--lg">
            <div className="modal-header">
              <h3>{editing ? 'Edit Product' : 'New Product'}</h3>
              <button onClick={() => setShowForm(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmit} className="modal-body">
              {/* Images section */}
              <div className="form-group">
                <label className="form-label">
                  Product Images <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>— up to {MAX_IMAGES}, first is primary</span>
                </label>
                <div className="media-grid">
                  {images.map((img, i) => (
                    <div key={i} className="media-tile">
                      <img src={img.url} alt={img.alt || ''} />
                      {i === 0 && <span className="media-tile__badge">Primary</span>}
                      <div className="media-tile__overlay">
                        <button type="button" title="Move left" disabled={i === 0} onClick={() => moveImage(i, -1)}>←</button>
                        <button type="button" title="Move right" disabled={i === images.length - 1} onClick={() => moveImage(i, 1)}>→</button>
                        <button type="button" title="Remove" className="media-tile__remove" onClick={() => removeImage(i)}>✕</button>
                      </div>
                    </div>
                  ))}
                  {images.length < MAX_IMAGES && (
                    <label className="media-add">
                      <input type="file" accept="image/*" multiple onChange={handleImageSelect} style={{ display:'none' }} />
                      <div style={{ textAlign:'center' }}>
                        <div style={{ fontSize: '1.6rem' }}>📷</div>
                        <p style={{ fontSize: '0.78rem', fontWeight: 600, marginTop: 4 }}>
                          {uploadingImage ? 'Uploading…' : 'Add image'}
                        </p>
                        <p style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 2 }}>
                          {images.length}/{MAX_IMAGES}
                        </p>
                      </div>
                    </label>
                  )}
                </div>
              </div>

              {/* Video section */}
              <div className="form-group">
                <label className="form-label">
                  Product Video <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>— optional, MP4/WebM/MOV up to 100MB</span>
                </label>
                {videoUrl ? (
                  <div className="video-preview">
                    <video src={videoUrl} controls preload="metadata" />
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <label className="btn btn-outline" style={{ padding: '6px 14px', fontSize: '0.82rem', cursor: 'pointer' }}>
                        <input type="file" accept="video/*" onChange={handleVideoSelect} style={{ display:'none' }} />
                        Replace
                      </label>
                      <button type="button" className="btn btn-outline" onClick={clearVideo} style={{ padding: '6px 14px', fontSize: '0.82rem' }}>Remove</button>
                    </div>
                  </div>
                ) : (
                  <label className="video-dropzone">
                    <input type="file" accept="video/*" onChange={handleVideoSelect} style={{ display:'none' }} />
                    <div style={{ textAlign:'center' }}>
                      <div style={{ fontSize: '1.8rem', marginBottom: 4 }}>🎬</div>
                      <p style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                        {uploadingVideo ? 'Uploading video…' : 'Click to upload video'}
                      </p>
                      <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>MP4, WebM, MOV — max 100MB</p>
                    </div>
                  </label>
                )}
              </div>

              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Product Name (EN) *</label>
                  <input name="name" value={form.name} onChange={e => { handleChange(e); if (!editing) setForm(f => ({ ...f, slug: autoSlug(e.target.value) })); }} className="form-control" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Product Name (AR)</label>
                  <input name="name_ar" value={form.name_ar || ''} onChange={handleChange} className="form-control" dir="rtl" />
                </div>
              </div>
              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">URL Slug *</label>
                  <input name="slug" value={form.slug} onChange={handleChange} className="form-control" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select name="category_id" value={form.category_id || ''} onChange={handleChange} className="form-control">
                    <option value="">No Category</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Base Price (KWD) *</label>
                  <input name="base_price" type="number" step="0.001" value={form.base_price} onChange={handleChange} className="form-control" required />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    Compare Price <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>— original "was" price (higher than Base)</span>
                  </label>
                  <input name="compare_price" type="number" step="0.001" value={form.compare_price ?? ''} onChange={handleChange} className="form-control" placeholder="e.g. 12.000" />
                  {(() => {
                    const base = parseFloat(form.base_price) || 0;
                    const cmp = parseFloat(form.compare_price) || 0;
                    if (cmp > base && base > 0) {
                      const pct = Math.round((1 - base / cmp) * 100);
                      return (
                        <p style={{ fontSize: '0.78rem', color: '#16a34a', marginTop: 4, fontWeight: 600 }}>
                          ✓ Discount: −{pct}% (KD {(cmp - base).toFixed(3)} off)
                        </p>
                      );
                    }
                    if (cmp > 0 && cmp <= base) {
                      return (
                        <p style={{ fontSize: '0.78rem', color: '#dc2626', marginTop: 4 }}>
                          ⚠ Compare Price must be higher than Base Price for the discount to show.
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">SKU</label>
                  <input name="sku" value={form.sku || ''} onChange={handleChange} className="form-control" />
                </div>
                <div className="form-group">
                  <label className="form-label">Origin Country</label>
                  <input name="origin_country" value={form.origin_country || ''} onChange={handleChange} className="form-control" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">
                  Stock Quantity <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>— units in stock; 0 = sold out (Add to Basket disabled)</span>
                </label>
                <input
                  name="stock_quantity" type="number" min="0" step="1"
                  value={form.stock_quantity ?? 0} onChange={handleChange}
                  className="form-control" placeholder="e.g. 25"
                />
                {(() => {
                  const s = parseInt(form.stock_quantity, 10);
                  if (!Number.isFinite(s)) return null;
                  if (s <= 0)  return <p style={{ fontSize:'0.78rem', color:'#dc2626', marginTop:4 }}>⚠ Out of stock — customers can't buy this.</p>;
                  if (s <= 5)  return <p style={{ fontSize:'0.78rem', color:'#d97706', marginTop:4 }}>🔥 Low stock — customers see "Only {s} left".</p>;
                  return <p style={{ fontSize:'0.78rem', color:'#16a34a', marginTop:4 }}>✓ {s} units available.</p>;
                })()}
              </div>
              <div className="form-group">
                <label className="form-label">Short Description</label>
                <input name="short_description" value={form.short_description || ''} onChange={handleChange} className="form-control" />
              </div>
              <div className="form-group">
                <label className="form-label">Full Description</label>
                <textarea name="description" value={form.description || ''} onChange={handleChange} className="form-control" rows="4" />
              </div>
              <div className="form-checkboxes">
                {[['is_active','Active'], ['is_featured','Featured'], ['is_bestseller','Bestseller']].map(([field, label]) => (
                  <label key={field} className="checkbox-label">
                    <input type="checkbox" name={field} checked={!!form[field]} onChange={handleChange} />
                    {label}
                  </label>
                ))}
              </div>
              <div style={{ display:'flex', gap:12, marginTop:20 }}>
                <button type="submit" className="btn btn-primary" disabled={saving || uploadingImage || uploadingVideo}>
                  {uploadingImage ? 'Uploading image…' : uploadingVideo ? 'Uploading video…' : saving ? 'Saving...' : (editing ? 'Update Product' : 'Create Product')}
                </button>
                <button type="button" className="btn btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .admin-card { background: white; border-radius: var(--radius-lg); padding: 20px; box-shadow: var(--shadow-sm); }
        .admin-table-wrap { overflow-x: auto; }
        .admin-table { width: 100%; border-collapse: collapse; }
        .admin-table th { text-align: left; padding: 10px 12px; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-muted); border-bottom: 2px solid var(--border); }
        .admin-table td { padding: 12px; font-size: 0.85rem; border-bottom: 1px solid #f5f5f5; vertical-align: middle; }
        .admin-table tr:hover td { background: #fafafa; }
        .action-btn { padding: 6px 14px; background: var(--cream); border-radius: 6px; font-size: 0.78rem; font-weight: 600; font-family: var(--font-sans); transition: all 0.2s; }
        .action-btn:hover { background: var(--gold); color: white; }
        .action-btn--danger:hover { background: #e53e3e; color: white; }
        .admin-pagination { display: flex; align-items: center; justify-content: center; gap: 16px; margin-top: 20px; font-size: 0.88rem; }
        .admin-pagination button { padding: 8px 16px; border-radius: 8px; background: var(--cream); font-family: var(--font-sans); }
        .admin-pagination button:disabled { opacity: 0.4; }
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 2000; display: flex; align-items: center; justify-content: center; padding: 20px; }
        .modal { background: white; border-radius: var(--radius-lg); width: 100%; max-width: 480px; max-height: 90vh; overflow-y: auto; }
        .modal--lg { max-width: 680px; }
        .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; border-bottom: 1px solid var(--border); position: sticky; top: 0; background: white; z-index: 1; }
        .modal-header h3 { font-family: var(--font-serif); }
        .modal-body { padding: 24px; }
        .form-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .form-checkboxes { display: flex; gap: 20px; flex-wrap: wrap; }
        .checkbox-label { display: flex; align-items: center; gap: 8px; font-size: 0.88rem; font-weight: 500; cursor: pointer; }

        .media-grid {
          display: grid; grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
          gap: 10px;
        }
        .media-tile {
          position: relative; aspect-ratio: 1; border-radius: var(--radius);
          overflow: hidden; border: 1px solid var(--border); background: var(--cream);
        }
        .media-tile img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .media-tile__badge {
          position: absolute; top: 6px; left: 6px; background: var(--gold); color: white;
          font-size: 0.62rem; font-weight: 700; padding: 2px 6px; border-radius: 4px;
          text-transform: uppercase; letter-spacing: 0.5px;
        }
        .media-tile__overlay {
          position: absolute; inset: 0; background: rgba(0,0,0,0.45);
          display: flex; align-items: center; justify-content: center; gap: 4px;
          opacity: 0; transition: opacity 0.18s;
        }
        .media-tile:hover .media-tile__overlay { opacity: 1; }
        .media-tile__overlay button {
          width: 28px; height: 28px; border-radius: 6px; background: rgba(255,255,255,0.92);
          color: #2c2416; font-size: 0.85rem; font-weight: 700; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
        }
        .media-tile__overlay button:disabled { opacity: 0.35; cursor: not-allowed; }
        .media-tile__overlay button:not(:disabled):hover { background: white; }
        .media-tile__remove:not(:disabled):hover { background: #e53e3e !important; color: white; }
        .media-add {
          aspect-ratio: 1; border: 2px dashed var(--border); border-radius: var(--radius);
          display: flex; align-items: center; justify-content: center; cursor: pointer;
          background: var(--cream); transition: all 0.2s;
        }
        .media-add:hover { border-color: var(--gold); background: rgba(212,148,58,0.06); }

        .video-dropzone {
          display: block; min-height: 130px; border: 2px dashed var(--border);
          border-radius: var(--radius); display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.2s; background: var(--cream);
        }
        .video-dropzone:hover { border-color: var(--gold); background: rgba(212,148,58,0.06); }
        .video-preview video {
          width: 100%; max-height: 280px; border-radius: var(--radius);
          background: #000; display: block;
        }

        @media (max-width: 480px) { .form-grid-2 { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
};

export default AdminProducts;
